import { createStore } from 'solid-js/store'

/**
 * El carrito del comprador, en el navegador.
 *
 * Vive en `localStorage` y no en el servidor porque quien compra no tiene
 * cuenta: pedirle que se registre para guardar un carrito es justo lo que hace
 * que no compre. Se pierde si cambia de dispositivo, y es un precio razonable.
 *
 * Va **separado por empresa**: el catálogo es de una empresa concreta
 * (`products.key` sólo es único dentro de ella) y mezclar artículos de dos
 * tiendas en un mismo pedido no tiene sentido, porque el cobro sale de la
 * pasarela de una sola.
 *
 * Se guarda lo mínimo para volver a pintar la línea sin pedir nada al servidor
 * (nombre, precio, imagen), pero **el precio que se cobra lo recalcula siempre
 * el backend** contra el catálogo: lo de aquí es para enseñar, no para cobrar.
 */

const STORAGE_KEY = 'fj.cart.v1'

export interface CartLineType {
  productId: number
  variantId: number | null
  /** Para volver a la ficha desde el carrito. */
  key: string
  name: string
  /** "Talla 42 · Negro", vacío si el producto no tiene variantes. */
  variantLabel?: string
  /** Sólo para mostrar; el cobro usa el precio del catálogo. */
  unitPrice: number
  currency: string
  image?: string | null
  quantity: number
  /** 0 o menos = no se controlan existencias (un servicio, por ejemplo). */
  stock: number
  trackInventory: boolean
}

interface CartState {
  /** Líneas por empresa (slug). */
  byCompany: Record<string, CartLineType[]>
}

/** Dos líneas son la misma si coinciden producto y variante. */
const sameLine = (a: CartLineType, productId: number, variantId: number | null) => a.productId === productId && (a.variantId ?? null) === (variantId ?? null)

function load(): CartState {
  if (typeof localStorage === 'undefined') return { byCompany: {} }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { byCompany: {} }
    const parsed = JSON.parse(raw) as unknown
    // No se confía en lo que haya en storage: lo escribe el usuario y una
    // versión vieja de la app pudo dejar otra forma. Si no cuadra, se descarta.
    if (!parsed || typeof parsed !== 'object') return { byCompany: {} }
    const byCompany = (parsed as CartState).byCompany
    if (!byCompany || typeof byCompany !== 'object') return { byCompany: {} }

    const limpio: Record<string, CartLineType[]> = {}
    for (const [slug, lines] of Object.entries(byCompany)) {
      if (!Array.isArray(lines)) continue
      limpio[slug] = lines.filter((l): l is CartLineType => Boolean(l) && typeof l.productId === 'number' && typeof l.name === 'string' && typeof l.quantity === 'number' && l.quantity > 0)
    }
    return { byCompany: limpio }
  } catch {
    return { byCompany: {} }
  }
}

const [cart, setCart] = createStore<CartState>(load())

function persist() {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart))
  } catch {
    // Modo privado o almacenamiento lleno: el carrito sigue funcionando en
    // memoria durante la visita, que es lo que importa para cerrar la compra.
  }
}

/** Las líneas del carrito de esa empresa. */
export const cartLines = (empresa: string): CartLineType[] => cart.byCompany[empresa] ?? []

/** Cuántas unidades hay en total (lo que se pinta en el icono). */
export const cartCount = (empresa: string): number => cartLines(empresa).reduce((sum, l) => sum + l.quantity, 0)

/** Lo que suman las líneas. Es orientativo: el total real lo calcula el backend. */
export const cartSubtotal = (empresa: string): number => cartLines(empresa).reduce((sum, l) => sum + l.unitPrice * l.quantity, 0)

/**
 * Añade (o suma) una línea. Si el producto controla existencias, la cantidad se
 * recorta a lo que queda: es mejor avisar aquí que dejar que falle al pagar.
 * Devuelve true si se pudo añadir todo lo pedido.
 */
export function addToCart(empresa: string, line: CartLineType): boolean {
  const actuales = cartLines(empresa)
  const index = actuales.findIndex(l => sameLine(l, line.productId, line.variantId))
  const pedida = (index >= 0 ? actuales[index].quantity : 0) + line.quantity

  const tope = line.trackInventory && line.stock > 0 ? line.stock : Number.POSITIVE_INFINITY
  const final = Math.min(pedida, tope)

  const siguientes = index >= 0 ? actuales.map((l, i) => (i === index ? { ...l, quantity: final } : l)) : [...actuales, { ...line, quantity: final }]

  setCart('byCompany', empresa, siguientes)
  persist()
  return final === pedida
}

/** Cambia la cantidad de una línea. A cero o menos, se quita. */
export function setLineQuantity(empresa: string, productId: number, variantId: number | null, quantity: number): void {
  if (quantity <= 0) {
    removeFromCart(empresa, productId, variantId)
    return
  }

  const siguientes = cartLines(empresa).map(l => {
    if (!sameLine(l, productId, variantId)) return l
    const tope = l.trackInventory && l.stock > 0 ? l.stock : Number.POSITIVE_INFINITY
    return { ...l, quantity: Math.min(quantity, tope) }
  })
  setCart('byCompany', empresa, siguientes)
  persist()
}

export function removeFromCart(empresa: string, productId: number, variantId: number | null): void {
  setCart(
    'byCompany',
    empresa,
    cartLines(empresa).filter(l => !sameLine(l, productId, variantId))
  )
  persist()
}

/** Vacía el carrito de esa empresa. Se llama al confirmarse el pedido. */
export function clearCart(empresa: string): void {
  setCart('byCompany', empresa, [])
  persist()
}

// ── Compra inmediata ────────────────────────────────────────────────────────

/**
 * "Comprar" no toca el carrito: quien le da a comprar quiere ESE artículo, y si
 * llevaba otras cosas apartadas debe encontrarlas intactas al volver. Por eso la
 * compra inmediata viaja aparte, en su propio hueco.
 *
 * Vive en `sessionStorage` (no en memoria) para que sobreviva a la redirección a
 * la pasarela y a un F5 en la página de pago, pero no se quede ahí para siempre.
 */
const BUY_NOW_KEY = 'fj.buyNow.v1'

export interface BuyNowType {
  empresa: string
  line: CartLineType
}

export function setBuyNow(value: BuyNowType): void {
  try {
    sessionStorage.setItem(BUY_NOW_KEY, JSON.stringify(value))
  } catch {
    // Sin sessionStorage la compra inmediata no puede continuar; la página de
    // pago lo detecta al no encontrar nada y manda de vuelta al catálogo.
  }
}

export function getBuyNow(empresa: string): CartLineType | null {
  try {
    const raw = sessionStorage.getItem(BUY_NOW_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as BuyNowType
    if (parsed?.empresa !== empresa || !parsed?.line?.productId) return null
    return parsed.line
  } catch {
    return null
  }
}

export function clearBuyNow(): void {
  try {
    sessionStorage.removeItem(BUY_NOW_KEY)
  } catch {
    // Nada que limpiar.
  }
}

export { cart }
